import { useCallback, useEffect, useState } from 'react'

type PrintRequest<Job extends string> = { job: Job, title: string }

/**
 * Which document to print, and a way to ask for one.
 *
 * Asking only records the job. The document renders on the next commit, and the
 * print dialog opens after that, so it always prints what is on the page — the
 * finished pick order's cartons, for instance, once completing has written
 * them to the cache. The job clears when the dialog closes.
 *
 * The browser names a saved PDF after the page title, so the title is swapped
 * for the document's own name while the dialog is open and put back after.
 */
export function usePrintJob<Job extends string>() {
	const [ request, setRequest ] = useState<PrintRequest<Job> | null>( null )

	useEffect( () => {
		if ( !request ) {
			return undefined
		}
		const previousTitle = document.title
		document.title = request.title
		const done = () => setRequest( null )
		window.addEventListener( 'afterprint', done )
		window.print()
		return () => {
			window.removeEventListener( 'afterprint', done )
			document.title = previousTitle
		}
	}, [ request ] )

	const print = useCallback( ( job: Job, title: string ) => setRequest( { job, title } ), [] )

	return { job: request?.job ?? null, print }
}
